import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { TableSkeletonRows, SkeletonBlock } from '../components/Skeleton.js';
import { EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { fmtDate, fmtDateTime } from '../lib/date.js';

type WebhookEvent =
  | 'CLIENT_WON'
  | 'ENGAGEMENT_WON'
  | 'ITEM_COMPLETED'
  | 'PROPOSAL_SIGNED'
  | 'INVOICE_PAID'
  | 'COMPLIANCE_INSTANCE_OVERDUE';

const EVENT_LABELS: Record<WebhookEvent, string> = {
  CLIENT_WON: 'Client won',
  ENGAGEMENT_WON: 'Engagement won',
  ITEM_COMPLETED: 'Item completed',
  PROPOSAL_SIGNED: 'Proposal signed',
  INVOICE_PAID: 'Invoice paid',
  COMPLIANCE_INSTANCE_OVERDUE: 'Compliance item overdue',
};

type WebhookRow = {
  id: string;
  url: string;
  events: WebhookEvent[];
  description: string | null;
  status: 'ACTIVE' | 'DISABLED';
  lastDeliveryAt: string | null;
  createdAt: string;
  lastDelivery: { status: 'PENDING' | 'SUCCESS' | 'FAILED'; httpStatus: number | null; createdAt: string } | null;
};

type DeliveryRow = {
  id: string;
  eventType: WebhookEvent;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  httpStatus: number | null;
  responseBody: string | null;
  attempt: number;
  deliveredAt: string | null;
  nextRetryAt: string | null;
  createdAt: string;
};

type CreatedWebhook = { id: string; url: string; events: WebhookEvent[]; signingSecret: string };

type ModalMode = { kind: 'new' } | { kind: 'edit'; hook: WebhookRow } | null;

export function WebhooksPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalMode>(null);
  const [deleting, setDeleting] = useState<WebhookRow | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedWebhook | null>(null);

  const { canDo } = useCapabilities();
  const canAdmin = canDo('webhooks.manage');

  const list = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const res = await fetch('/v1/webhooks', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: WebhookRow[] } };
      return body.data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (input: { url: string; events: WebhookEvent[]; description?: string }) => {
      const res = await fetch('/v1/webhooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; data: CreatedWebhook }>;
    },
    onSuccess: (body) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setModal(null);
      setCreated({ ...body.data });
      body.data.signingSecret = '';
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: { url?: string; events?: WebhookEvent[]; description?: string | null; status?: 'ACTIVE' | 'DISABLED' } }) => {
      const res = await fetch(`/v1/webhooks/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setModal(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/webhooks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setDeleting(null);
    },
  });

  const test = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/webhooks/${id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; data: { deliveryId: string } }>;
    },
    onSuccess: (_body, id) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', id] });
      setExpanded(id);
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Webhooks</h1>
          <p className="text-sm text-muted">{list.data?.length ?? '—'} endpoints · deliveries are signed HMAC-SHA256 and retried with backoff</p>
        </div>
        {canAdmin && (
          <button onClick={() => setModal({ kind: 'new' })} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New webhook</button>
        )}
      </header>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">URL</th>
              <th className="px-4 py-3 font-medium">Events</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last delivery</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={5} />}
            {list.error && <tr><td colSpan={5} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>}
            {!list.isLoading && !list.error && list.data?.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">No webhooks configured.</td></tr>
            )}
            {list.data?.map((w) => (
              <WebhookTableRow
                key={w.id}
                hook={w}
                canAdmin={canAdmin}
                expanded={expanded === w.id}
                onToggle={() => { if (canAdmin) setExpanded(expanded === w.id ? null : w.id); }}
                onEdit={() => setModal({ kind: 'edit', hook: w })}
                onDelete={() => setDeleting(w)}
                onTest={() => test.mutate(w.id)}
                testing={test.isPending && test.variables === w.id}
                onToggleStatus={() => update.mutate({ id: w.id, input: { status: w.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' } })}
              />
            ))}
          </tbody>
        </table>
      </div>

      {test.error && <p className="text-xs text-rose-400">{test.error.message}</p>}
      {update.error && !modal && <p className="text-xs text-rose-400">{update.error.message}</p>}

      {modal && (
        <WebhookModal
          mode={modal}
          onClose={() => setModal(null)}
          onCreate={(input) => create.mutate(input)}
          onUpdate={(id, input) => update.mutate({ id, input })}
          isPending={create.isPending || update.isPending}
          error={(create.error?.message ?? update.error?.message) || null}
        />
      )}

      {created && canAdmin && (
        <ModalShell title="Webhook created" onClose={() => { setCreated(null); create.reset(); }}>
          <p className="text-sm text-white">Copy this one-time signing key now to configure your verifying receiver. It is not available in listings or after closing.</p>
          <input aria-label="Webhook signing key" type="password" autoComplete="off" readOnly value={created.signingSecret} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white font-mono mt-3" />
          <div className="flex justify-end gap-3 mt-4">
            <button type="button" onClick={() => void navigator.clipboard.writeText(created.signingSecret)} className="text-sm text-accent">Copy signing key</button>
            <button onClick={() => { setCreated(null); create.reset(); }} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">Done</button>
          </div>
        </ModalShell>
      )}

      {deleting && (
        <ModalShell title="Delete webhook" onClose={() => setDeleting(null)}>
          <p className="text-sm text-white">Delete the webhook for <span className="font-mono text-xs">{deleting.url}</span>? It will stop receiving events immediately.</p>
          {remove.error && <p className="text-xs text-rose-400 mt-2">{remove.error.message}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setDeleting(null)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button type="button" onClick={() => remove.mutate(deleting.id)} disabled={remove.isPending} className="bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg">{remove.isPending ? 'Deleting…' : 'Delete'}</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function WebhookTableRow({
  hook: w,
  canAdmin,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onTest,
  testing,
  onToggleStatus,
}: {
  hook: WebhookRow;
  canAdmin: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
  testing: boolean;
  onToggleStatus: () => void;
}) {
  return (
    <>
      <tr className="border-b border-border/50 hover:bg-bg-hover/40 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3">
          <div className="text-white font-mono text-xs max-w-xs truncate">{w.url}</div>
          {w.description && <div className="text-xs text-muted truncate">{w.description}</div>}
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {w.events.map((e) => (
              <span key={e} className="px-2 py-0.5 rounded text-xs bg-bg-hover text-white">{EVENT_LABELS[e] ?? e}</span>
            ))}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={w.status === 'ACTIVE'
            ? 'px-2 py-0.5 rounded text-xs border pill-success'
            : 'px-2 py-0.5 rounded text-xs border pill-neutral'}>
            {w.status === 'ACTIVE' ? 'Active' : 'Disabled'}
          </span>
        </td>
        <td className="px-4 py-3 text-muted text-xs">
          {w.lastDelivery ? (
            <span className={w.lastDelivery.status === 'SUCCESS' ? 'text-emerald-300' : w.lastDelivery.status === 'FAILED' ? 'text-rose-400' : 'text-amber-300'}>
              {w.lastDelivery.status}{w.lastDelivery.httpStatus ? ` · HTTP ${w.lastDelivery.httpStatus}` : ''} · {fmtDate(w.lastDelivery.createdAt)}
            </span>
          ) : 'Never'}
        </td>
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <button onClick={onToggle} className="text-xs text-muted hover:text-white mr-3">{expanded ? 'Hide' : 'Deliveries'}</button>
          {canAdmin && (
            <>
              <button onClick={onTest} disabled={testing} className="text-xs text-muted hover:text-white border border-border hover:border-accent px-2 py-1 rounded mr-3 disabled:opacity-50">{testing ? 'Sending…' : 'Test'}</button>
              <button onClick={onToggleStatus} className="text-xs text-muted hover:text-white mr-3">{w.status === 'ACTIVE' ? 'Disable' : 'Enable'}</button>
              <EditIcon onClick={onEdit} label="Edit" />
              <DeleteIcon onClick={onDelete} label="Delete" />
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border/50">
          <td colSpan={5} className="px-4 pb-4 pt-1">
            <DeliveryLog webhookId={w.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function DeliveryLog({ webhookId }: { webhookId: string }) {
  const deliveries = useQuery({
    queryKey: ['webhook-deliveries', webhookId],
    refetchInterval: 15000,
    queryFn: async () => {
      const res = await fetch(`/v1/webhooks/${webhookId}/deliveries`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: DeliveryRow[] } };
      return body.data.items;
    },
  });

  if (deliveries.isLoading) return <SkeletonBlock rows={2} className="py-2" />;
  if (deliveries.error) return <p className="text-xs text-rose-400 py-2">{(deliveries.error as Error).message}</p>;
  if (deliveries.data?.length === 0) return <p className="text-xs text-muted italic py-2">No deliveries yet.</p>;

  return (
    <div className="overflow-x-auto">
    <table className="w-full text-xs min-w-[560px]">
      <thead>
        <tr className="text-left text-muted border-b border-border/50">
          <th className="py-1.5 font-medium">Event</th>
          <th className="py-1.5 font-medium">Status</th>
          <th className="py-1.5 font-medium">HTTP</th>
          <th className="py-1.5 font-medium">Attempts</th>
          <th className="py-1.5 font-medium">Time</th>
          <th className="py-1.5 font-medium">Response</th>
        </tr>
      </thead>
      <tbody>
        {deliveries.data?.map((d) => (
          <tr key={d.id} className="border-b border-border/30">
            <td className="py-1.5 text-white">{EVENT_LABELS[d.eventType] ?? d.eventType}</td>
            <td className={`py-1.5 ${d.status === 'SUCCESS' ? 'text-emerald-300' : d.status === 'FAILED' ? 'text-rose-400' : 'text-amber-300'}`}>
              {d.status}{d.status === 'PENDING' && d.nextRetryAt ? ` · retries ${fmtDateTime(d.nextRetryAt)}` : ''}
            </td>
            <td className="py-1.5 text-muted">{d.httpStatus ?? '—'}</td>
            <td className="py-1.5 text-muted">{d.attempt}</td>
            <td className="py-1.5 text-muted">{fmtDateTime(d.deliveredAt ?? d.createdAt)}</td>
            <td className="py-1.5 text-muted max-w-[220px] truncate" title={d.responseBody ?? undefined}>{d.responseBody ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function WebhookModal({
  mode,
  onClose,
  onCreate,
  onUpdate,
  isPending,
  error,
}: {
  mode: { kind: 'new' } | { kind: 'edit'; hook: WebhookRow };
  onClose: () => void;
  onCreate: (input: { url: string; events: WebhookEvent[]; description?: string }) => void;
  onUpdate: (id: string, input: { url?: string; events?: WebhookEvent[]; description?: string | null; status?: 'ACTIVE' | 'DISABLED' }) => void;
  isPending: boolean;
  error: string | null;
}) {
  const isEdit = mode.kind === 'edit';
  const hook = isEdit ? mode.hook : null;
  const [form, setForm] = useState({
    url: hook?.url ?? '',
    events: hook?.events ?? [] as WebhookEvent[],
    description: hook?.description ?? '',
    active: hook ? hook.status === 'ACTIVE' : true,
  });

  function toggleEvent(e: WebhookEvent) {
    setForm((f) => ({ ...f, events: f.events.includes(e) ? f.events.filter((x) => x !== e) : [...f.events, e] }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isEdit) {
      onUpdate(hook!.id, {
        url: form.url,
        events: form.events,
        description: form.description.trim() || null,
        status: form.active ? 'ACTIVE' : 'DISABLED',
      });
    } else {
      onCreate({ url: form.url, events: form.events, ...(form.description.trim() && { description: form.description.trim() }) });
    }
  }

  return (
    <ModalShell title={isEdit ? 'Edit webhook' : 'New webhook'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Payload URL</label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white font-mono"
            placeholder="https://example.com/ops-webhook"
            required
          />
          <p className="text-[11px] text-muted mt-1">Public http(s) only — internal addresses are refused.</p>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Events</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {(Object.keys(EVENT_LABELS) as WebhookEvent[]).map((e) => (
              <label key={e} className="flex items-center gap-2 text-sm text-white">
                <input type="checkbox" checked={form.events.includes(e)} onChange={() => toggleEvent(e)} />
                {EVENT_LABELS[e]}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Description</label>
          <input
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            maxLength={500}
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-white">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
            Active
          </label>
        )}
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={isPending || form.events.length === 0} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}


